import { useTranslation } from '@/hooks/use-translation';
import { useEffect } from 'react';

import { useMicrophonePermission } from '@/hooks/permissions/use-microphone-permission';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';

import browserBarImage from '@/assets/images/microphone-blocked-browser-bar.png';
import { isDesktop } from '@/lib/platform';

type MicrophonePermissionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const MicrophonePermissionModal = ({ isOpen, onClose }: MicrophonePermissionModalProps) => {
  const { t } = useTranslation('desktop_ui');
  const { hasMicrophonePermission, requestMicrophonePermission, openMicSystemSettings } =
    useMicrophonePermission();

  const isBlocked = hasMicrophonePermission === 'denied';
  const isBlockedOnDesktop = isBlocked && isDesktop();

  useEffect(() => {
    if (hasMicrophonePermission === 'granted') {
      onClose();
    }
  }, [hasMicrophonePermission, onClose]);

  const handleBlockedClick = () => {
    if (isBlockedOnDesktop) {
      openMicSystemSettings();
    } else {
      onClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        onPointerDownOutside={(e) => e.preventDefault()}
        showCloseIcon={false}
        className="flex w-[360px] flex-col gap-0 overflow-hidden rounded-2xl border border-primary-10 bg-white p-0 shadow-lg"
      >
        {/* Image (blocked variant only — browser instructions, not relevant on desktop) */}
        {isBlocked && !isBlockedOnDesktop && (
          <div className="flex items-center justify-center pl-4 pt-4">
            <img src={browserBarImage} alt="" className="w-full object-contain" />
          </div>
        )}

        {/* Body */}
        <div className="flex flex-col gap-1 p-4">
          <DialogTitle className="text-heading-18 text-primary-100">
            {isBlocked
              ? t('components.microphone_permission_modal.blocked_title')
              : t('components.microphone_permission_modal.enable_title')}
          </DialogTitle>
          <DialogDescription className="text-body-12 text-primary-60">
            {isBlockedOnDesktop
              ? t('components.microphone_permission_modal.blocked_description_desktop')
              : isBlocked
                ? t('components.microphone_permission_modal.blocked_description')
                : t('components.microphone_permission_modal.enable_description')}
          </DialogDescription>
        </div>

        {/* Footer */}
        <DialogFooter className="flex-row justify-end border-t border-primary-5 p-3">
          <button
            type="button"
            onClick={isBlocked ? handleBlockedClick : requestMicrophonePermission}
            className="h-7 cursor-pointer rounded-md px-2 text-body-13 text-white shadow"
            style={{
              background: 'linear-gradient(210deg, #006647 10%, #004734 90%)',
            }}
          >
            {isBlockedOnDesktop
              ? t('components.microphone_permission_modal.open_settings')
              : isBlocked
                ? t('components.microphone_permission_modal.close')
                : t('components.microphone_permission_modal.allow_button')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
